import * as React from 'react';
import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useShallow } from 'zustand/react/shallow';
import { useMediaApplicationContext } from '../../components/MediaApplicationContext';
import useMediaStyles from '../../components/useMediaStyles';
import LangEnum from '../../lang/LangEnum';
import { mediaSorterAsc } from '../../lib/MediasLib';
import useGeneralSettingsStore from '../../stores/GeneralSettings/useGeneralSettingsStore';
import { MediaFormatEnum } from '../general-settings/enums/mediaFormat.enum';
import { MediaType } from '../media/mimeType';
import { useTranslation } from '../translation/TranslationContext';

const useStyles = makeStyles()((theme) => ({
  img: {
    borderRadius: theme.spacing(1),
    position: 'absolute',
    display: 'block',
    width: '100%',
    height: '100%',
    aspectRatio: '16/10',
    top: 0,
    left: 0,
    objectFit: 'cover',
  },
  borderRadius: {
    borderRadius: theme.spacing(1),
  },
}));

type MinimumMedia = {
  type: MediaType;
  position: number;
  url?: string;
};
type ProductAvatarRendererProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
> & {
  medias?: MinimumMedia[];
  url?: string;
};

const ProductAvatarRenderer = ({
  url,
  medias,
  className,
  ...props
}: ProductAvatarRendererProps) => {
  const mediaContext = useMediaApplicationContext();
  const { translate } = useTranslation();
  const { classes, cx } = useStyles();
  const { mediaFormat } = useGeneralSettingsStore(
    useShallow((state) => ({
      mediaFormat: state.generalSettings.mediaFormat,
    })),
  );
  const { classes: mediaClasses } = useMediaStyles({
    imageAspectRatioPadding: mediaFormat === MediaFormatEnum.SQUARE ? '100%' : undefined,
  });
  const firstMedia = useMemo(() => {
    if (!medias) {
      return null;
    }

    const images = medias.filter((media) => media.type === 'IMAGE');
    const sortedMedias = images.sort(mediaSorterAsc);
    return sortedMedias[0];
  }, [medias]);

  const withoutUrl = !url || url?.length === 0;

  return (
    <div className={cx(mediaClasses.mediaBoxWithAspectRatio, classes.borderRadius, className)}>
      <img
        className={cx(withoutUrl ? mediaClasses.mediaContentCover : '', classes.img)}
        src={mediaContext.getMediaFromSize(url || firstMedia?.url, 'xs')}
        alt={
          url || firstMedia?.url
            ? translate(LangEnum.GLOBAL_PRODUCT_IMAGE)
            : translate(LangEnum.GLOBAL_NO_IMAGE_AVAILABLE)
        }
        loading="lazy"
        {...props}
      />
    </div>
  );
};

export default React.memo<ProductAvatarRendererProps>(ProductAvatarRenderer);
